<template>
  <!-- Main viewer container -->
  <div class="viewer-container">
    <div id="container">
      <!-- Logo or title section (optional) -->
      <div class="logo">
        <img :src="ASSETS.IMAGES.LOGO" alt="LooksY"/>
      </div>

      <!-- Description text -->
      <div class="desc">
        <!-- If there are any avatars, ask user to pick one. Otherwise ask them to create a new one -->
        {{ avatars && avatars.length
          ? "Lütfen düzenlemek istediğiniz avatarı seçin."
          : "Merhaba! Başlamak için bir avatar oluşturun." 
        }}
      </div>

      <!-- Main content area (vertically centered) -->
      <div class="center-vertical">
        
        <!-- Avatar grid -->
        <div class="avatar-wrapper">
          <!-- One card per avatar -->
          <div 
            class="avatar" 
            v-for="(avatar, index) in avatars" 
            :key="avatar.id || index"
          >
            <!-- If we have a render_url, show the image -->
            <template v-if="avatar.render_url">
              <img
                :src="getProxyImageUrl(avatar.render_url)"
                :alt="`avatar_${index}`"
                @error="handleImageError"
              />
            </template>
            <!-- Otherwise, show a spinner or placeholder icon -->
            <div v-else class="placeholder">
              <div class="placeholder-content">
                <div class="avatar-placeholder"></div>
              </div>
              <span>Yükleniyor</span>
            </div>

            <!-- Overlay with Edit/Delete buttons -->
            <div class="overlay">
              <div class="button" @click.stop="avatarSelected(avatar)">Düzenle</div>
              <div class="button" @click.stop="deleteAvatar(avatar)">Sil</div>
            </div>
          </div>
        </div>

        <!-- Avatar creation button -->
        <div class="create-button" @click="createAvatar">
          {{ buttonText }}
        </div>

        <!-- Separator text -->
        <div class="or">ya da</div>

        <!-- Mannequin view button -->
        <div class="view-button" @click="baseView">
          Manken Üzerinde İncele
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ASSETS } from "@/constants/urls";

export default {
  name: "AvatarList",
  props: {
    /** Array of user avatars (each with .id, .render_url, etc.) */
    avatars: {
      type: Array,
      default: () => [],
    },
    /** Callback to delete an avatar */
    deleteAvatar: {
      type: Function,
      required: true,
    },
    /** Callback to edit/select an avatar */
    avatarSelected: {
      type: Function,
      required: true,
    },
    /** Go back or switch view mode */
    back: {
      type: Function,
      required: true,
    },
    /** View base mannequin instead of a custom avatar */
    baseView: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      /** Text displayed on the "create avatar" button */
      buttonText: "Avatar Oluştur",
      /** Assets object for referencing images/icons in the template */
      ASSETS,
    };
  },
  methods: {
    /**
     * Create a new avatar, unless the max of 2 is reached
     */
    createAvatar() {
      if (!this.avatars || this.avatars.length < 2) {
        this.back("init");
      } else {
        // Show an error or disable the button
        this.buttonText = "Yalnızca 2 adet avatar oluşturabilirsiniz.";
      }
    },
    
    /**
     * Handle image load errors
     */
    handleImageError(event) {
      console.warn('Failed to load avatar image:', event);
      // Replace the failed image with a placeholder
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMjAwIDEyMDAiPg0KICAgIDxyZWN0IHdpZHRoPSIxMjAwIiBoZWlnaHQ9IjEyMDAiIGZpbGw9IiM0NDQiIC8+DQogICAgPGNpcmNsZSBjeD0iNjAwIiBjeT0iNDAwIiByPSIyNDAiIGZpbGw9IiM3Nzc3NzciIC8+DQogICAgPGNpcmNsZSBjeD0iNjAwIiBjeT0iOTUwIiByPSI0MDAiIGZpbGw9IiM3Nzc3NzciIC8+DQo8L3N2Zz4=';
    },
    
    /**
     * Get a proxy URL to avoid CORS issues
     * Use direct URL in production, this is just for local development
     */
    getProxyImageUrl(url) {
      if (!url) return '';
      
      // For local development, you might need to use a data URL as fallback
      // In production, the URL should work directly if CORS is properly configured
      return 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMjAwIDEyMDAiPg0KICAgIDxyZWN0IHdpZHRoPSIxMjAwIiBoZWlnaHQ9IjEyMDAiIGZpbGw9IiM0NDQiIC8+DQogICAgPGNpcmNsZSBjeD0iNjAwIiBjeT0iNDAwIiByPSIyNDAiIGZpbGw9IiM3Nzc3NzciIC8+DQogICAgPGNpcmNsZSBjeD0iNjAwIiBjeT0iOTUwIiByPSI0MDAiIGZpbGw9IiM3Nzc3NzciIC8+DQo8L3N2Zz4=';
    }
  },
  mounted() {
    console.log("AvatarList mounted, avatars:", this.avatars);
  }
};
</script>

<style scoped>
/* Container layout */
#container {
  display: flex;
  flex-direction: column;
  text-align: center;
}

/* Logo area */
.logo {
  width: 150px;
  margin: 20px auto 10px auto;
}

/* Center main content vertically */
.center-vertical {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
}

/* Description text */
.desc {
  font-size: 16px;
  color: #ffffff;
  margin: 10px 0 20px 0;
}

/* Avatar grid wrapper */
.avatar-wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 15px;
  max-height: 350px;
  overflow-y: auto;
  margin-bottom: 20px;
}

/* Each avatar card */
.avatar {
  width: 110px;
  height: 170px;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background-color: #333;
  cursor: pointer;
  transition: transform 0.3s ease;
}
.avatar:hover {
  transform: scale(0.98);
}

/* The image inside each card */
.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Placeholder if no image yet */
.placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #444;
  width: 100%;
  height: 100%;
  background-color: #333;
  gap: 5px;
}
.placeholder-content {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 70%;
}
.avatar-placeholder {
  width: 60px;
  height: 60px;
  border-radius: 30px;
  background-color: #666;
  margin-bottom: 10px;
}
.spinner-icon {
  width: 24px;
  height: 24px;
}

/* Hover overlay with Edit/Delete */
.overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  opacity: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  align-items: center;
  transition: opacity 0.3s;
}
.avatar:hover .overlay {
  opacity: 1;
}
.overlay .button {
  padding: 4px 8px;
  background: #222;
  color: #ccc;
  border-radius: 4px;
  font-size: 14px;
}
.overlay .button:hover {
  background: #555;
  color: #fff;
}

/* Separator text ("ya da") */
.or {
  color: #f7f7f7;
  margin: 15px 0;
}

/* Create avatar button */
.create-button {
  background: transparent;
  border: 1px solid #f7f7f7;
  color: #f7f7f7;
  padding: 10px 20px;
  max-width: 200px;
  border-radius: 10px;
  /* margin: 20px auto 0 auto; */
  cursor: pointer;
  transition: all 0.3s;
  /* margin-top: 40px; */
  margin-bottom: 40px;
}
.create-button:hover {
  background: #f7f7f7;
  color: #333;
}

/* "Manken Üzerinde İncele" link */
.view-button {
  color: #ccc;
  text-decoration: underline;
  font-size: 15px;
  margin-top: 15px;
  cursor: pointer;
}
</style>